import { useDispatch, useSelector } from 'react-redux';
import getOpPlusV3Info from 'store/dashboard/selector/getOpPlusV3Info';
import { differenceInDays, parseISO } from 'date-fns';
import { useEffect } from 'react';
import fetchPlusMembershipDataOperation from 'store/dashboard/operation/fetchPlusMembershipDataOperation';

export interface MembershipStatus {
  isCancelled: boolean;
  isCancelledBeforeRenewal: boolean;
  isMembershipWithdrawn: boolean;
  isLegacyOpPlus: boolean;
  legacyExpirationDays?: number;
  legacyExpiringSoon?: boolean;
  isFailedPaymentPaused?: boolean;
  isFailedPaymentPausedFirst7Days?: boolean;
  isFailedPaymentPausedFirst30Days?: boolean;
  isFailedPaymentCancelled?: boolean;
  isActiveLessThan14Days?: boolean;
  isPlusActive?: boolean;
}

const usePlusMembershipStatus = (): MembershipStatus => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchPlusMembershipDataOperation());
  }, []);

  const subscriptionData = useSelector(getOpPlusV3Info);

  const isMembershipCancelled =
    subscriptionData?.subscriptionInfo?.status === 'non-renewing' &&
    !subscriptionData?.subscriptionInfo?.isActiveOpPlusMember;

  const isMembershipCancelledBeforeRenewal =
    subscriptionData?.subscriptionInfo?.status === 'non-renewing' &&
    subscriptionData?.subscriptionInfo?.isActiveOpPlusMember;

  const isMembershipWithdrawn = subscriptionData?.subscriptionInfo?.status === 'withdrawn';

  const isLegacyOpPlus =
    (subscriptionData?.legacyMemberNotification && subscriptionData?.legacyMemberNotification.displayNotification) ||
    false;

  const isRenewedInLessThan14Days = (renewalDate: string) => {
    const dateObject = new Date(renewalDate);
    dateObject.setFullYear(dateObject.getFullYear() - 1);
    return differenceInDays(new Date(), parseISO(dateObject.toISOString())) < 14;
  };

  const legacyExpirationDays = subscriptionData?.legacyMemberNotification?.info?.expirationDays;
  const legacyExpiringSoon = Boolean(legacyExpirationDays && legacyExpirationDays <= 30);
  const isFailedPaymentPaused = subscriptionData?.subscriptionInfo.status === 'failed-payment-paused';
  const isFailedPaymentCancelled = subscriptionData?.subscriptionInfo.status === 'failed-payment-cancelled';
  const isFailedPaymentPausedFirst7Days =
    isFailedPaymentPaused &&
    subscriptionData?.subscriptionInfo.renewalFailedDate &&
    differenceInDays(new Date(), parseISO(subscriptionData?.subscriptionInfo.renewalFailedDate)) < 7;
  const isFailedPaymentPausedFirst30Days =
    isFailedPaymentPaused &&
    subscriptionData?.subscriptionInfo.renewalFailedDate &&
    differenceInDays(new Date(), parseISO(subscriptionData?.subscriptionInfo.renewalFailedDate)) < 30;
  const isActiveLessThan14Days =
    subscriptionData?.subscriptionInfo?.isActiveOpPlusMember &&
    (differenceInDays(new Date(), parseISO(subscriptionData?.subscriptionInfo.joinDate)) < 14 ||
      (Boolean(subscriptionData?.subscriptionInfo.renewalDate) &&
        isRenewedInLessThan14Days(subscriptionData?.subscriptionInfo.renewalDate)));
  const isPlusActive = subscriptionData?.subscriptionInfo?.isActiveOpPlusMember;

  return {
    isCancelled: isMembershipCancelled || false,
    isCancelledBeforeRenewal: isMembershipCancelledBeforeRenewal || false,
    isLegacyOpPlus,
    legacyExpirationDays,
    legacyExpiringSoon,
    isFailedPaymentPaused,
    isMembershipWithdrawn,
    isFailedPaymentPausedFirst7Days: Boolean(isFailedPaymentPausedFirst7Days),
    isFailedPaymentPausedFirst30Days: Boolean(isFailedPaymentPausedFirst30Days),
    isFailedPaymentCancelled,
    isActiveLessThan14Days,
    isPlusActive,
  };
};

export default usePlusMembershipStatus;
